"use client";

import { Spinner } from "@/components/ui/spinner";

import { Lock, LockOpen, Save, Send } from "lucide-react";

import { ProcessInstanceIdCopy } from "@/components/process-instance-id-copy";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface TaskFooterProps {
  onSave: () => Promise<void>;
  onSubmit: () => Promise<void>;
  isSaving: boolean;
  isSubmitting: boolean;
  processInstanceId?: string;
  disabled?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;
}

export function TaskFooter({
  onSave,
  onSubmit,
  isSaving,
  isSubmitting,
  processInstanceId,
  disabled = false,
  submitDisabled = false,
  submitLabel = "Enviar",
}: TaskFooterProps) {
  const statusLabel = disabled ? "Solo lectura" : "Edicion habilitada";

  return (
    <>
      <Separator className="h-px bg-gray-200" />
      <div className={cn(
        "bg-white px-4 py-4 transition-all duration-200",
        disabled && "bg-gray-50"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-col gap-1 text-sm text-gray-500">
            <div
              className="ml-2 flex items-center"
              aria-label={statusLabel}
              title={statusLabel}
            >
              {disabled ? (
                <Lock className="h-3 w-3 text-amber-600" />
              ) : (
                <LockOpen className="h-3 w-3 text-green-400" />
              )}
            </div>

            {processInstanceId ? (
              <ProcessInstanceIdCopy
                processInstanceId={processInstanceId}
                className="ml-2"
                textClassName="max-w-[min(48vw,34rem)] leading-none text-gray-500"
              />
            ) : null}
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onSave}
              disabled={disabled || isSaving || isSubmitting}
              className={cn(
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {isSaving ? (
                <Spinner className="mr-2" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isSaving ? "Guardando..." : "Guardar"}
            </Button>

            <Button
              onClick={onSubmit}
              disabled={disabled || isSaving || isSubmitting || submitDisabled}
              className={cn(
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {isSubmitting ? (
                <Spinner className="mr-2" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {isSubmitting ? "Enviando..." : submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
